import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { getNewPage } from '../../helpers/utils/utils';

/**
 * Generaliza o boilerplate "reset síncrono + guard duplo (idProcessoRef +
 * requestRef monotônico) + fetch paginado/ordenável com semântica de
 * substituição (não concatenação)" — 3ª ocorrência no módulo (Títulos,
 * AW2-1034 Etapa 2; Antecedentes, AW2-1033 Etapa 2), extraído na Etapa 2 de
 * AW2-1054 pela regra "2x aceitável, 3ª extrai" (`src/prototipo/CLAUDE.md`).
 *
 * `buscarPagina(idProcesso, pagina, tamanho, ordenacao, extra)` é chamado a
 * cada fetch (reset por processo, mudança de ordenação/página/tamanho, ou
 * `buscar` disparado manualmente) — deve retornar uma Promise resolvendo
 * `{ content, totalElements }`. Não precisa de `useCallback` no chamador: a
 * referência mais recente é sempre usada via ref interno (mesmo padrão
 * "latest ref" já estabelecido no módulo), então `buscarPagina` pode fechar
 * sobre estado do chamador (ex.: um filtro extra) sem invalidar o efeito de
 * reset por processo.
 *
 * `extra` é um valor opcional de passagem livre (não interpretado pelo
 * hook) — existe para o caso de um filtro adicional que precisa ser
 * explícito no momento da chamada (não capturado por closure), replicando o
 * mesmo padrão já usado por `tipoConsultaAntecedentes`/
 * `onAlternarTipoConsultaAntecedentes`, onde o valor não pode vir de closure
 * porque a troca do filtro e o refetch acontecem no mesmo handler, antes do
 * próximo render (closures atrasariam 1 ciclo).
 *
 * NÃO usar este hook para paginação por scroll-infinito com concatenação
 * (ex.: `historicos` da aba Histórico, Etapa 2 de AW2-1054) — esse caso tem
 * semântica de dado fundamentalmente diferente (concatena páginas em vez de
 * substituir, sem ordenação selecionável pelo usuário) e foi deixado de fora
 * deliberadamente, para não forçar uma abstração que não serve aos dois
 * casos igualmente bem.
 */
export default function usePaginacaoOrdenacaoPorProcesso(
  idProcesso,
  buscarPagina,
  { tamanhoPaginaPadrao, ordenacaoPadrao, extraPadrao },
) {
  const [dados, setDados] = useState(null);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(0);
  const [tamanhoPagina, setTamanhoPagina] = useState(tamanhoPaginaPadrao);
  const [ordenacao, setOrdenacao] = useState(ordenacaoPadrao);

  const idProcessoRef = useRef(idProcesso);
  idProcessoRef.current = idProcesso;
  const requestRef = useRef(0);
  const buscarPaginaRef = useRef(buscarPagina);
  buscarPaginaRef.current = buscarPagina;

  const buscar = useCallback((idProcessoAlvo, paginaAlvo, tamanhoAlvo, ordenacaoAlvo, extraAlvo) => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;

    const aindaValido = () => idProcessoRef.current === idProcessoAlvo
      && requestRef.current === requestId;

    buscarPaginaRef.current(idProcessoAlvo, paginaAlvo, tamanhoAlvo, ordenacaoAlvo, extraAlvo)
      .then(({ content, totalElements }) => {
        if (aindaValido()) {
          setDados(content);
          setTotal(totalElements ?? 0);
        }
      })
      .catch(() => {
        // Falha de rede não pode deixar o card preso em "Carregando..." para
        // sempre — resolve para vazio, mesmo padrão já usado nas 2
        // ocorrências que originaram este hook.
        if (aindaValido()) {
          setDados([]);
          setTotal(0);
        }
      });
  }, []);

  useEffect(() => {
    setDados(null);
    setTotal(0);
    setPagina(0);
    setTamanhoPagina(tamanhoPaginaPadrao);
    setOrdenacao(ordenacaoPadrao);

    if (!idProcesso) {
      return;
    }

    buscar(idProcesso, 0, tamanhoPaginaPadrao, ordenacaoPadrao, extraPadrao);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idProcesso, buscar]);

  const onChangeOrdenacao = (campo, extra = extraPadrao) => {
    const novaOrdenacao = {
      campo,
      direcao: ordenacao.direcao === 'ASC' ? 'DESC' : 'ASC',
    };
    setOrdenacao(novaOrdenacao);
    buscar(idProcesso, pagina, tamanhoPagina, novaOrdenacao, extra);
  };

  const onChangePagina = (novaPagina, extra = extraPadrao) => {
    setPagina(novaPagina);
    buscar(idProcesso, novaPagina, tamanhoPagina, ordenacao, extra);
  };

  const onChangeTamanhoPagina = (event, extra = extraPadrao) => {
    const novoTamanho = Number(event.target.value);
    const novaPagina = getNewPage(tamanhoPagina, pagina, novoTamanho);
    setTamanhoPagina(novoTamanho);
    setPagina(novaPagina);
    buscar(idProcesso, novaPagina, novoTamanho, ordenacao, extra);
  };

  return {
    dados,
    total,
    pagina,
    tamanhoPagina,
    ordenacao,
    onChangeOrdenacao,
    onChangePagina,
    onChangeTamanhoPagina,
    // Setters crus + `buscarComParametros`, expostos para o caso de um
    // handler específico da feature precisar compor um reset combinado (ex.:
    // trocar um filtro extra E resetar página/ordenação num único clique,
    // como `onAlternarTipoConsultaAntecedentes` em `ControleProcessosPage.jsx`)
    // sem reimplementar o guard/fetch — os 3 handlers acima cobrem só o caso
    // comum de "1 dimensão muda por vez".
    setPagina,
    setOrdenacao,
    setTamanhoPagina,
    buscarComParametros: buscar,
  };
}
